import { Box, Button, Collapse, Paper, Stack, type SxProps, type Theme } from '@mui/material';
import { useId, useState, type FormEventHandler, type ReactElement, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

export interface ListFilterPanelProps {
  readonly activeSecondaryFilterCount?: number;
  readonly applyLabel?: string;
  readonly ariaLabel: string;
  readonly clearLabel?: string;
  readonly extraActions?: ReactNode;
  readonly onClear?: () => void;
  readonly onSubmit?: FormEventHandler<HTMLFormElement>;
  readonly primaryFields: ReactNode;
  readonly secondaryFields?: ReactNode;
  readonly sx?: SxProps<Theme>;
}

export const listFilterGridSx = {
  display: 'grid',
  gap: 1.5,
  gridTemplateColumns: {
    xs: 'minmax(0, 1fr)',
    sm: 'repeat(2, minmax(0, 1fr))',
    lg: 'repeat(3, minmax(0, 1fr))',
    xl: 'repeat(4, minmax(0, 1fr))',
  },
  '@media (min-width: 1440px)': {
    gridTemplateColumns: 'repeat(4, minmax(0, 1fr))',
  },
  '& > *': { minWidth: 0, width: '100%' },
} as const;

export function ListFilterPanel({
  activeSecondaryFilterCount = 0,
  applyLabel,
  ariaLabel,
  clearLabel,
  extraActions,
  onClear,
  onSubmit,
  primaryFields,
  secondaryFields,
  sx,
}: ListFilterPanelProps): ReactElement {
  const { t } = useTranslation();
  const [expanded, setExpanded] = useState(false);
  const secondaryId = useId();
  const hasActions = onSubmit !== undefined || onClear !== undefined || extraActions !== undefined;
  const countLabel =
    activeSecondaryFilterCount > 0
      ? ` · ${t('filterPanel.activeCount', { count: activeSecondaryFilterCount })}`
      : '';

  return (
    <Paper
      aria-label={ariaLabel}
      component={onSubmit === undefined ? 'section' : 'form'}
      noValidate={onSubmit === undefined ? undefined : true}
      onSubmit={onSubmit}
      sx={[
        { display: 'grid', gap: 1.5, mb: 2, p: 2 },
        ...(sx === undefined ? [] : Array.isArray(sx) ? sx : [sx]),
      ]}
      variant="outlined"
    >
      <Box data-slot="list-filter-primary" sx={listFilterGridSx}>
        {primaryFields}
      </Box>
      {secondaryFields === undefined ? null : (
        <>
          <Button
            aria-controls={secondaryId}
            aria-expanded={expanded}
            onClick={() => setExpanded((current) => !current)}
            sx={{ justifySelf: 'start' }}
            type="button"
          >
            {t(expanded ? 'filterPanel.fewer' : 'filterPanel.more')}
            {countLabel}
          </Button>
          <Collapse in={expanded}>
            <Box
              aria-label={t('filterPanel.secondary')}
              data-slot="list-filter-secondary"
              id={secondaryId}
              role="group"
              sx={listFilterGridSx}
            >
              {secondaryFields}
            </Box>
          </Collapse>
        </>
      )}
      {hasActions ? (
        <Stack
          data-slot="list-filter-actions"
          direction={{ xs: 'column', sm: 'row' }}
          spacing={1}
          sx={{
            justifyContent: 'flex-end',
            '& .MuiButton-root': { minHeight: 40, width: { xs: '100%', sm: 'auto' } },
          }}
        >
          {onSubmit === undefined ? null : (
            <Button type="submit" variant="contained">
              {applyLabel ?? t('actions.apply')}
            </Button>
          )}
          {onClear === undefined ? null : (
            <Button
              onClick={() => {
                setExpanded(false);
                onClear();
              }}
              type="button"
            >
              {clearLabel ?? t('actions.clear')}
            </Button>
          )}
          {extraActions}
        </Stack>
      ) : null}
    </Paper>
  );
}
